<template>
  <MarketingPageTemplate
    :title="data.title || 'Website-Traffic'"
    :subtitle="data.subtitle || 'Besuche, Interaktionen und Conversions nach Kampagne und Zugang'"
    :loading="loading"
    :loaded-once="loadedOnce"
    :error="error"
    :legend="data.legend || []"
    loading-text="Lade Traffic-Daten…"
    @retry="reload"
  >
    <template #channel-filters>
      <div class="fpair">
        <span class="flabel">Zugang</span>
        <SingleDropdown
          label="Zugang"
          :options="sourceTypeOptions"
          :model-value="sourceType"
          @update:model-value="(value) => setExtra({ source_type: value })"
        />
      </div>
      <div class="fpair">
        <span class="flabel">Link / QR-Code</span>
        <SingleDropdown
          label="Link / QR-Code"
          :options="sourceLabelOptions"
          :model-value="sourceLabel"
          @update:model-value="(value) => setExtra({ source_label: value })"
        />
      </div>
      <div class="fpair">
        <span class="flabel">Landingpage</span>
        <SingleDropdown
          label="Landingpage"
          :options="landingOptions"
          :model-value="landingPage"
          @update:model-value="(value) => setExtra({ landing_page: value })"
        />
      </div>
    </template>

    <template #kpis>
      <!-- Kennzahlen wie bisher: Sitzungen · Interagierte Sitzungen · Engagement-Rate,
           Conversions und Conversion-Rate nur, sofern erfasst (liefert die API dann mit). -->
      <MarketingKpis :columns="3">
        <KpiTile v-if="kpi('sessions')" :card="kpi('sessions')" />
        <KpiTile v-if="kpi('engaged_sessions')" :card="kpi('engaged_sessions')" />
        <!-- UX: "Qualität der Besuche" als Meta-Zeile an der Engagement-Rate statt eigener Karte. -->
        <KpiTile
          v-if="kpi('engagement_rate')"
          :card="kpi('engagement_rate')"
          :meta="sessions ? `${formatNumber(engagedSessions)} von ${formatNumber(sessions)} Sitzungen mit Engagement` : ''"
        />
      </MarketingKpis>
      <MarketingKpis v-if="kpi('conversions')" :cards="[kpi('conversions'), kpi('conversion_rate')].filter(Boolean)" :columns="2" />
    </template>

    <template #main>
      <DashboardSection title="Sitzungen im Zeitraum" :empty="!dates.length">
        <MarketingChart :categories="dates" :series="dateSeries" datetime x-title="Datum" />
      </DashboardSection>
    </template>

    <DashboardSection title="Traffic nach Zugang" :empty="!sourceNames.length">
      <!-- UX: Bei nur einer Kategorie eine Textzeile statt eines Diagramms mit einem Balken. -->
      <p v-if="sourceBreakdown.length === 1" class="mb-summary-line">
        Alle <strong>{{ formatNumber(sourceBreakdown[0].sessions) }}</strong> Sitzungen kamen über
        <strong>{{ sourceBreakdown[0].name }}</strong>, davon
        <strong>{{ formatNumber(sourceBreakdown[0].engaged) }}</strong> mit Engagement.
      </p>
      <MarketingChart v-else :categories="sourceNames" :series="sourceSeries" x-title="Zugang" />
    </DashboardSection>

    <DashboardSection title="Traffic nach Maßnahme und Zugang" :empty="!tableRows.length">
      <TableScroll>
        <table class="jr-list">
          <thead>
            <tr>
              <th scope="col">Kampagne</th>
              <th scope="col">Maßnahme</th>
              <th scope="col">Zugang</th>
              <th scope="col">Link / QR-Code</th>
              <th scope="col">Landingpage</th>
              <th scope="col" class="num">Sitzungen</th>
              <th scope="col" class="num">Interagierte Sitzungen</th>
              <th scope="col" class="num">Engagement-Rate</th>
              <!-- UX: Leere Spalten ausblenden – solange keine Conversions erfasst sind. -->
              <th v-if="showConversions" scope="col" class="num">Conversions</th>
              <th v-if="showConversions" scope="col" class="num">Conversion-Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableRows" :key="index">
              <td>{{ row.campaign }}</td>
              <!-- UX: Anzeigenamen statt technischer Bezeichner ("fol_kooperation", "/"), Original im Tooltip. -->
              <td :title="row.measure">{{ humanizeName(row.measure) }}</td>
              <td>{{ row.source_type_label }}</td>
              <td :title="row.source_label">{{ humanizeName(row.source_label) }}</td>
              <td :title="row.landing_page">{{ landingPageLabel(row.landing_page) }}</td>
              <td class="num">{{ formatNumber(row.sessions) }}</td>
              <td class="num">{{ formatNumber(row.engaged_sessions) }}</td>
              <td class="num">{{ formatPercent(row.engagement_rate) }}</td>
              <td v-if="showConversions" class="num">{{ row.conversions == null ? '–' : formatNumber(row.conversions) }}</td>
              <td v-if="showConversions" class="num">{{ formatPercent(row.conversion_rate) }}</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
    </DashboardSection>
  </MarketingPageTemplate>
</template>

<script setup>
import { computed } from 'vue';
import SingleDropdown from '@/features/dashboard/components/SingleDropdown.vue';
import MarketingPageTemplate from '../components/MarketingPageTemplate.vue';
import DashboardSection from '../components/DashboardSection.vue';
import MarketingKpis from '../components/MarketingKpis.vue';
import KpiTile from '../components/KpiTile.vue';
import MarketingChart from '../components/MarketingChart.vue';
import TableScroll from '../components/TableScroll.vue';
import { useMarketingDashboard } from '../composables/useMarketingDashboard';
import { useMarketingFilters } from '../composables/useMarketingFilters';
import { humanizeName, landingPageLabel } from '../composables/displayNames';
import { columnHasData } from '../composables/tableUtils';
import { formatNumber, formatPercent } from '../composables/formatters';

const props = defineProps({
  filterOptions: { type: Object, default: () => ({}) }
});

const { extraValue, setExtra } = useMarketingFilters();
const sourceType = computed(() => extraValue('source_type', ''));
const sourceLabel = computed(() => extraValue('source_label', ''));
const landingPage = computed(() => extraValue('landing_page', ''));
const extraParams = computed(() => ({
  source_type: sourceType.value,
  source_label: sourceLabel.value,
  landing_page: landingPage.value
}));
const { data, loading, error, loadedOnce, reload } = useMarketingDashboard('/mb/traffic/', extraParams);

// Filterwerte bleiben technisch (API-Parameter), nur die Beschriftung wird lesbar.
const sourceTypeOptions = computed(() => [
  { value: '', label: 'Alle Zugänge' },
  ...(props.filterOptions.source_types || []).map((item) => ({ value: item.code, label: item.name }))
]);
const sourceLabelOptions = computed(() => [
  { value: '', label: 'Alle Links / QR-Codes' },
  ...(props.filterOptions.source_labels || []).map((item) => ({ value: item, label: humanizeName(item) }))
]);
const landingOptions = computed(() => [
  { value: '', label: 'Alle Landingpages' },
  ...(props.filterOptions.landing_pages || []).map((item) => ({ value: item, label: landingPageLabel(item) }))
]);

const tableRows = computed(() => data.value.table || []);
const showConversions = computed(() => columnHasData(tableRows.value, 'conversions'));
const dates = computed(() => (data.value.series?.by_date || []).map((item) => item.date));
const dateSeries = computed(() => [
  { name: 'Sitzungen', data: (data.value.series?.by_date || []).map((item) => item.sessions) },
  { name: 'Interagierte Sitzungen', data: (data.value.series?.by_date || []).map((item) => item.engaged_sessions) }
]);

function kpi(key) {
  return (data.value.kpis || []).find((item) => item.key === key) || null;
}

const sessions = computed(() => kpi('sessions')?.value || 0);
const engagedSessions = computed(() => kpi('engaged_sessions')?.value || 0);

const sourceBreakdown = computed(() => {
  const map = {};
  tableRows.value.forEach((row) => {
    const key = row.source_type || 'other';
    if (!map[key]) {
      map[key] = {
        name: row.source_type_label || key,
        sessions: 0,
        engaged: 0
      };
    }
    map[key].sessions += row.sessions || 0;
    map[key].engaged += row.engaged_sessions || 0;
  });
  return Object.values(map);
});
const sourceNames = computed(() => sourceBreakdown.value.map((item) => item.name));
const sourceSeries = computed(() => [
  { name: 'Sitzungen', data: sourceBreakdown.value.map((item) => item.sessions) },
  { name: 'Interagierte Sitzungen', data: sourceBreakdown.value.map((item) => item.engaged) }
]);
</script>
